import type { AnalyticsClient } from '../analytics/client';
import { ANALYTICS_SCHEMA_VERSION, type AnalyticsIdentity } from '../analytics/contract';
import type { DiagnosticUploader } from '../analytics/diagnostic-uploader';
import type { SessionTracker } from '../analytics/session-tracker';
import type { GameAudio } from '../audio/audio';
import type { BotStrategySnapshot } from '../bot/bot';
import type { ContentSnapshot } from '../engine/types';
import { stableStringify } from '../engine/checksum';
import { ENGINE_VERSION } from '../engine/engine';
import { createDiagnosticBundle, type DiagnosticTerminationReason, type MatchDiagnosticBundle } from '../diagnostics/analyzer';
import type { I18nPort } from '../i18n/i18n-service';
import { NetworkApiClient, configuredNetworkBaseUrl } from '../network/api-client';
import type { ClaimStatus, RoomSession } from '../network/contracts';
import { playReplay } from '../replay/playback';
import type { ReplayFile } from '../replay/replay';
import { clearDiagnosticRecoveryBundle, saveDiagnosticBundle } from '../storage/diagnostics-store';
import type { AppSettings } from './settings';
import type { CompletedMatchRuntime } from './controllers/match-controller';
import type { LobbyController } from './controllers/lobby-controller';
import type { ResultController } from './controllers/result-controller';
import type { ToastController } from './controllers/toast-controller';

export interface MatchFinalizationContext {
  readonly replay: boolean;
  readonly botStrategy: BotStrategySnapshot | null;
  readonly networkSession: RoomSession | null;
}

export interface MatchFinalizerOptions {
  readonly content: ContentSnapshot;
  readonly settings: AppSettings;
  readonly analyticsIdentity: AnalyticsIdentity;
  readonly analytics: AnalyticsClient;
  readonly sessionTracker: SessionTracker;
  readonly diagnosticUploader: DiagnosticUploader;
  readonly audio: GameAudio;
  readonly i18n: I18nPort;
  readonly lobby: LobbyController;
  readonly result: ResultController;
  readonly toast: ToastController;
}

export class MatchFinalizer {
  private readonly networkApi = new NetworkApiClient(configuredNetworkBaseUrl());

  constructor(private readonly options: MatchFinalizerOptions) {}

  async finish(runtime: CompletedMatchRuntime, context: MatchFinalizationContext): Promise<MatchDiagnosticBundle> {
    const { engine } = runtime;
    await runtime.diagnosticsSettled;
    this.options.audio.stopMusic();
    this.options.audio.play('match-end', engine.journal, engine.currentTick);
    await this.options.audio.flushDiagnostics(engine.journal);
    let checksumVerified = false;
    try {
      const replayed = playReplay(engine.replay, this.options.content);
      checksumVerified = replayed.checksum() === engine.checksum();
      if (!checksumVerified) engine.journal.record({ tick: engine.currentTick, phase: 'simulation', source: 'replay-verifier', level: 'error', code: 'DET_CHECKSUM_MISMATCH', payload: { live: engine.checksum(), replay: replayed.checksum() } });
    } catch (error) {
      engine.journal.record({ tick: engine.currentTick, phase: 'simulation', source: 'replay-verifier', level: 'error', code: 'DET_CHECKSUM_MISMATCH', payload: { error: String(error) } });
    }
    if (!context.replay && runtime.driverKind === 'bot') await this.options.lobby.learnFrom(engine, context.botStrategy);

    let claimStatus: ClaimStatus | null = null;
    if (runtime.driverKind === 'network' && context.networkSession && engine.state.result) {
      claimStatus = await this.confirmNetworkClaim(context.networkSession, engine.replay, engine.checksum()).catch((error: unknown) => {
        engine.journal.record({ tick: engine.currentTick, phase: 'presentation', source: 'result-claim', level: 'warn', code: 'NETWORK_CLAIM_FAILED', payload: { error: String(error) } });
        return null;
      });
      this.options.toast.show(this.options.i18n.t(claimStatus?.status === 'peer-confirmed' ? 'network.claimConfirmed' : claimStatus?.status === 'disputed' ? 'network.claimDisputed' : 'network.claimPending'));
    }

    const resultTrust = claimStatus?.resultTrust ?? runtime.context.resultTrust;
    const replay: ReplayFile = { ...engine.replay, match: { ...engine.replay.match, resultTrust } };
    let bundle = createDiagnosticBundle({ journal: engine.journal, replay, checkpoints: engine.checkpoints, result: engine.state.result, engineVersion: ENGINE_VERSION, contentVersion: this.options.content.version, arenaId: engine.state.arenaId, seed: engine.state.seed, currentTick: engine.currentTick, terminationReason: 'completed', debug: runtime.driverDebug });
    let bundleSaved = false;
    try {
      await saveDiagnosticBundle(bundle, this.options.settings.uploadDiagnosticLogs && !context.replay);
      bundleSaved = true;
    } catch (error) {
      engine.journal.sinkErrors += 1;
      bundle = createDiagnosticBundle({ journal: engine.journal, replay, checkpoints: engine.checkpoints, result: engine.state.result, engineVersion: ENGINE_VERSION, contentVersion: this.options.content.version, arenaId: engine.state.arenaId, seed: engine.state.seed, currentTick: engine.currentTick, terminationReason: 'completed', debug: runtime.driverDebug });
      this.options.toast.show(this.options.i18n.t('toast.logSaveFailed', { error: String(error) }));
    }

    if (context.replay) {
      this.options.sessionTracker.setMode('menu');
    } else if (bundle.summary.result) {
      this.options.sessionTracker.matchCompleted();
      this.options.analytics.match({
        schemaVersion: ANALYTICS_SCHEMA_VERSION,
        matchId: bundle.manifest.matchId,
        anonymousId: this.options.analyticsIdentity.anonymousId,
        sessionId: this.options.analyticsIdentity.sessionId,
        startedAt: runtime.startedAt,
        completedAt: new Date().toISOString(),
        arenaId: bundle.manifest.arenaId,
        rulesetId: bundle.manifest.rulesetId,
        engineVersion: bundle.manifest.engineVersion,
        contentVersion: bundle.manifest.contentVersion,
        matchMode: bundle.manifest.matchMode,
        resultTrust: bundle.manifest.resultTrust,
        networkMatchId: bundle.manifest.networkMatchId,
        botId: context.botStrategy?.botId ?? null,
        botVersion: context.botStrategy?.botVersion ?? null,
        durationTicks: bundle.summary.result.tick,
        tickRate: bundle.replay.rules.tickRate,
        result: bundle.summary.result,
        commandsAccepted: runtime.analytics.commandsAccepted,
        commandsRejected: runtime.analytics.commandsRejected,
        phases: runtime.analytics.phases,
        cardStats: runtime.analytics.cardStats,
        diagnosticMetrics: bundle.metrics,
        diagnosticCounts: { critical: bundle.summary.critical, errors: bundle.summary.errors, warnings: bundle.summary.warnings },
        topFindings: bundle.findings.slice(0, 10).map(({ ruleId, severity }) => ({ ruleId, severity })),
        checksumVerified,
      });
      this.options.analytics.event('match_completed', { matchId: bundle.manifest.matchId, winner: bundle.summary.result.winner, durationSeconds: bundle.summary.result.tick / bundle.replay.rules.tickRate, matchMode: bundle.manifest.matchMode, networkMatchId: bundle.manifest.networkMatchId });
      this.options.analytics.session(this.options.sessionTracker.snapshot());
    }
    if (bundleSaved) await clearDiagnosticRecoveryBundle(bundle.manifest.matchId).catch(() => { engine.journal.sinkErrors += 1; });
    void this.options.diagnosticUploader.process();
    this.options.result.show(bundle, engine.checksum());
    return bundle;
  }

  async abort(runtime: CompletedMatchRuntime, context: MatchFinalizationContext, reason: Exclude<DiagnosticTerminationReason, 'running' | 'completed'>): Promise<MatchDiagnosticBundle> {
    const { engine } = runtime;
    await runtime.diagnosticsSettled;
    this.options.audio.stopMusic();
    await this.options.audio.flushDiagnostics(engine.journal);
    engine.journal.record({ tick: engine.currentTick, phase: 'presentation', source: 'match-finalizer', level: 'error', code: 'MATCH_ABORTED', payload: { reason } });
    let bundle = createDiagnosticBundle({
      journal: engine.journal,
      replay: structuredClone(engine.replay),
      checkpoints: engine.checkpoints,
      result: null,
      engineVersion: ENGINE_VERSION,
      contentVersion: this.options.content.version,
      arenaId: engine.state.arenaId,
      seed: engine.state.seed,
      currentTick: engine.currentTick,
      terminationReason: reason,
      debug: runtime.driverDebug,
    });
    let bundleSaved = false;
    try {
      await saveDiagnosticBundle(bundle, this.options.settings.uploadDiagnosticLogs && !context.replay);
      bundleSaved = true;
    } catch (error) {
      engine.journal.sinkErrors += 1;
      bundle = createDiagnosticBundle({ journal: engine.journal, replay: structuredClone(engine.replay), checkpoints: engine.checkpoints, result: null, engineVersion: ENGINE_VERSION, contentVersion: this.options.content.version, arenaId: engine.state.arenaId, seed: engine.state.seed, currentTick: engine.currentTick, terminationReason: reason, debug: runtime.driverDebug });
      this.options.toast.show(this.options.i18n.t('toast.logSaveFailed', { error: String(error) }));
    }
    if (bundleSaved) await clearDiagnosticRecoveryBundle(bundle.manifest.matchId).catch(() => { engine.journal.sinkErrors += 1; });
    void this.options.diagnosticUploader.process();
    return bundle;
  }

  private async submitNetworkClaim(session: RoomSession, replay: ReplayFile, finalChecksum: string): Promise<ClaimStatus> {
    if (!replay.result) throw new Error('Cannot claim an unfinished match');
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(stableStringify({ seed: replay.seed, rules: replay.rules, arenaId: replay.arenaId, decks: replay.decks, commands: replay.commands, result: replay.result })));
    const replayHash = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
    return this.networkApi.submitClaim(session, { winner: replay.result.winner, finalTick: replay.result.tick, finalChecksum, replayHash });
  }

  private async confirmNetworkClaim(session: RoomSession, replay: ReplayFile, finalChecksum: string): Promise<ClaimStatus> {
    let status = await this.submitNetworkClaim(session, replay, finalChecksum);
    for (let attempt = 0; attempt < 10 && status.status === 'pending'; attempt += 1) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 500));
      status = await this.submitNetworkClaim(session, replay, finalChecksum);
    }
    return status;
  }
}
